<script setup>
import { ref, onMounted, computed } from 'vue';
import { Head } from '@inertiajs/vue3';
import { products, productCategories } from '../data/products';
import CustomLink from './Components/CustomLink.vue';
import Button from './Components/Button.vue'; // Add Button import
import AnimatedWords from './Components/AnimatedWords.vue';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

// Product section references for animations
const productRefs = ref([]);

// Helper function to get category info by ID
const getCategoryById = (id) => productCategories.find(cat => cat.id === id) || null;

// Calculate where to place CTAs within product list
const ctaPositions = computed(() => {
    const productCount = products.length;
    
    // If fewer than 8 items, only show CTA at the end
    if (productCount < 8) {
        return [productCount];
    }
    
    // If 8-11 items, show CTAs in the middle and at the end
    if (productCount < 12) {
        return [Math.floor(productCount / 2), productCount];
    }
    
    // For 12+ items, calculate positions with at least 6 items between CTAs
    const positions = [];
    const numCtas = Math.floor(productCount / 6);
    
    // If we can have at least 2 CTAs (meaning at least 12 products)
    if (numCtas >= 2) {
        // Calculate the spacing to distribute CTAs evenly
        const spacing = Math.ceil(productCount / numCtas);
        
        // Ensure spacing is at least 6
        const adjustedSpacing = Math.max(spacing, 6);
        
        // Add position for each CTA
        for (let i = 1; i <= numCtas; i++) {
            const position = Math.min(i * adjustedSpacing, productCount);
            
            // Avoid adding duplicate positions
            if (positions.length === 0 || position > positions[positions.length - 1]) {
                positions.push(position);
            }
        }
        
        // Ensure we have a CTA at the middle if not already close to it
        const middle = Math.floor(productCount / 2);
        const closestToMiddle = positions.reduce((prev, curr) => 
            Math.abs(curr - middle) < Math.abs(prev - middle) ? curr : prev
        );
        
        // If the closest CTA is far from the middle, add one at the middle
        if (Math.abs(closestToMiddle - middle) > adjustedSpacing / 3) {
            positions.push(middle);
            positions.sort((a, b) => a - b);
        }
        
        // Ensure the last position is the end of the list
        if (positions[positions.length - 1] !== productCount) {
            positions.push(productCount);
        }
    } else {
        // Default to middle and end for edge cases
        positions.push(Math.floor(productCount / 2), productCount);
    }
    
    // Filter positions to ensure at least 6 items between CTAs
    const filteredPositions = [positions[0]];
    for (let i = 1; i < positions.length; i++) {
        if (positions[i] - filteredPositions[filteredPositions.length - 1] >= 6) {
            filteredPositions.push(positions[i]);
        }
    }
    
    return filteredPositions;
});

// Animation setup
onMounted(() => {
    // Animate hero section
    gsap.from('.hero-title', { y: 30, opacity: 0, duration: 1.2, ease: 'power3.out' });
    gsap.from('.hero-description', { y: 30, opacity: 0, duration: 1.2, delay: 0.3, ease: 'power3.out' });
    
    // Animate products when scrolled into view
    productRefs.value.forEach((ref, index) => { 
        const delay = index % 2 === 0 ? 0 : 0.2; 
        gsap.fromTo(ref, 
            { y: 50, opacity: 0 }, 
            { y: 0, opacity: 1, duration: 1, delay, ease: 'power2.out', 
              scrollTrigger: { 
                  trigger: ref, 
                  start: 'top 80%', 
                  toggleActions: 'play none none none' 
              }
            }
        );
    });
});
</script>

<template>
    <Head>
        <!-- Primary Meta Tags -->
        <title>Onze Producten | c-o-d-e-s software development</title>
        <meta name="title" content="Onze Producten | c-o-d-e-s software development">
        <meta name="description" content="Ontdek ons complete portfolio van innovatieve softwareoplossingen, aangepast aan de behoeften van moderne bedrijven.">
        
        <!-- Open Graph / Facebook -->
        <meta property="og:type" content="website">
        <meta property="og:url" content="https://codes.nl/products">
        <meta property="og:title" content="Onze Producten | c-o-d-e-s software development">
        <meta property="og:description" content="Ontdek ons complete portfolio van innovatieve softwareoplossingen, aangepast aan de behoeften van moderne bedrijven.">
        <meta property="og:image" content="/img/og/products-social-banner.jpg">
        
        <!-- Twitter -->
        <meta property="twitter:card" content="summary_large_image">
        <meta property="twitter:url" content="https://codes.nl/products">
        <meta property="twitter:title" content="Onze Producten | c-o-d-e-s software development">
        <meta property="twitter:description" content="Ontdek ons complete portfolio van innovatieve softwareoplossingen, aangepast aan de behoeften van moderne bedrijven.">
        <meta property="twitter:image" content="/img/og/products-social-banner.jpg">
        
        <!-- Canonical URL -->
        <link rel="canonical" href="https://codes.nl/products">
    </Head>
            
    <div class="wide-container mt-16 sm:mt-20 md:mt-28">
        <div class="max-w-4xl mx-auto text-center">
            <h1 class="hero-title text-5xl sm:text-6xl font-bold text-gray-900 tracking-tight leading-[1.1]">
                <AnimatedWords :words="['Software', 'Platform', 'Dashboard', 'Applicatie', 'Webshop']" :interval="3000" extrawords="oplossingen" :minWidth="{ default: '100%', md: '19rem' }" :responsiveLayout="true" />
            </h1>
            <p class="hero-description mt-6 text-xl text-gray-600 max-w-3xl mx-auto">
                Wij ontwikkelen maatwerk software oplossingen voor verschillende branches. Elke oplossing is gebouwd met de nieuwste technologieën en afgestemd op de specifieke behoeften van jouw organisatie.
            </p>
        </div>
    </div>
    
    <!-- Products List -->
    <div class="wide-container">
        <div class="max-w-7xl mx-auto">
            <!-- Loop through products and insert CTAs at calculated positions -->
            <template v-for="(product, index) in products" :key="product.meta.id">
                <!-- Product Card -->
                <div ref="el => productRefs[index] = el" class="flex flex-col md:flex-row items-start gap-8 md:gap-16 mb-24 p-8 rounded-3xl transition-all duration-500" :class="[ index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse', `hover:bg-${product.style.projectColor}-50/30` ]">
                    <!-- Product Image -->
                    <div class="w-full md:w-2/5">
                        <!-- Add overflow-hidden to the wrapper div, not the image -->
                        <div class="overflow-hidden rounded-2xl mb-4 aspect-video ring-1 ring-gray-100 shadow-md">
                            <img :src="product.media.productsPageImage || product.media.featuredImage || product.media.mockupImages.mid.src" :alt="product.meta.name" class="w-full h-full object-cover object-center transform hover:scale-[102%] transition-transform duration-700" />
                        </div>
                        
                        <!-- Dynamic category pills -->
                        <div class="flex flex-wrap gap-2">
                            <div v-for="categoryId in product.meta.categoryIds || []" :key="categoryId" class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium" :class="[ `text-${product.style.projectColor}-600`, `bg-${product.style.projectColor}-100` ]">
                                <font-awesome-icon :icon="['fas', getCategoryById(categoryId)?.icon || 'tag']" class="mr-2 h-3.5 w-3.5" /> 
                                {{ getCategoryById(categoryId)?.name || 'Categorie' }}
                            </div>
                        </div>
                    </div>
                    
                    <!-- Product Info -->
                    <div class="w-full md:w-3/5">
                        <h2 class="text-3xl font-bold text-gray-900 mb-4">{{ product.content.title }}</h2>
                        <p class="text-lg text-gray-600 mb-6">{{ product.content.longDescription || product.content.description }}</p>
                        
                        <div class="flex flex-wrap gap-4 mb-8">
                            <span v-for="feature in product.features?.slice(0, 4)" :key="feature" class="text-sm bg-gray-100 rounded-full px-3 py-1 font-medium text-gray-700">{{ feature }}</span>
                        </div>
                        
                        <CustomLink :href="`/project/${product.meta.slug}`" class="inline-flex justify-center items-center px-5 py-2.5 rounded-full font-medium text-base tracking-wide cursor-pointer shadow-md hover:shadow-lg text-white transition-all duration-300 ease-[cubic-bezier(.5,.85,.25,1)] hover:-translate-y-1" :class="[ `bg-${product.style.projectColor}-500`, `hover:bg-${product.style.projectColor}-600` ]" >
                            <span class="mr-2">Meer details</span>
                            <font-awesome-icon :icon="['fas', 'arrow-right']" class="h-3.5 w-3.5 transition-transform duration-500 ease-[cubic-bezier(.5,.85,.25,1.8)] group-hover:translate-x-1" />
                        </CustomLink>
                    </div>
                </div>
                
                <!-- Insert CTA at calculated positions -->
                <div v-if="ctaPositions.includes(index + 1)" class="w-full my-20">
                    <div class="max-w-6xl mx-auto relative overflow-hidden bg-white rounded-3xl shadow-lg border border-gray-100">
                        <!-- Subtle background pattern that matches the page -->
                        <div class="absolute inset-0 bg-[linear-gradient(to_right,#4f4f4f0a_1px,transparent_1px),linear-gradient(to_bottom,#4f4f4f0a_1px,transparent_1px)] bg-[size:32px_32px] opacity-30"></div>
                        
                        <!-- Content designed to match product cards -->
                        <div class="relative z-10 px-8 py-14 md:p-16 flex flex-col lg:flex-row items-center justify-between gap-10">
                            <div class="text-left max-w-xl xl:max-w-2xl">
                                <h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Klaar voor een oplossing op maat?</h2>
                                <p class="text-lg text-gray-600">
                                    Benieuwd hoe onze software jouw business kan ondersteunen? <span class="hidden sm:block"></span>Neem contact op voor een vrijblijvend gesprek over de mogelijkheden.
                                </p>
                            </div>
                            
                            <!-- Button styled to match product card buttons -->
                            <Button href="/#client-testimonials" variant="gradient" icon="arrow-right" class="group">Ontdek jouw oplossing</Button>
                        </div>
                    </div>
                </div>
            </template>
        </div>
    </div>
</template>
